import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { UserMasterValidateUniqueIdentitiesRequestDTO } from 'src/app/master/users/users.interface';
import { UsersService } from 'src/app/master/users/users.service';


@Injectable({
  providedIn: 'root'
})
export class IdentityValidationService {

  public onUserDetected = new Subject<number>();
  constructor(private userMasterService: UsersService, private toastr: ToastrService) { }

  /**
   * Attach watchers to Aadhaar, PAN, Email, and Mobile controls
   */
  watchUniqueIdentifiers(form: FormGroup, options?: {
    userIdField?: string,
    fieldMap?: {
      AadharNumber?: string,
      PanNumber?: string,
      EmailId?: string,
      MobileNo?: string
    }
  }): Subscription[] {
    const subscriptions: Subscription[] = [];

    const map = {
      AadharNumber: options?.fieldMap?.AadharNumber || 'aadharNumber',
      PanNumber: options?.fieldMap?.PanNumber || 'panNumber',
      // EmailId: options?.fieldMap?.EmailId || 'emailId',
      // MobileNo: options?.fieldMap?.MobileNo || 'mobileNo'
      EmailId: '',
      MobileNo: ''
    };

    const controlsMap = {
      AadharNumber: form.get(map.AadharNumber),
      PanNumber: form.get(map.PanNumber),
      EmailId: form.get(map.EmailId),
      MobileNo: form.get(map.MobileNo)
    };

    Object.entries(controlsMap).forEach(([field, control]) => {
      if (control) {
        const sub = control.valueChanges
          .pipe(debounceTime(800), distinctUntilChanged())
          .subscribe((value) => {
            this.validateUniqueIdentities(field as any, value, form, options?.userIdField);
          });
        subscriptions.push(sub);
      }
    });

    return subscriptions;
  }

  /**
   * Validate unique identifiers against backend SP
   */
  validateUniqueIdentities(
    field: 'AadharNumber' | 'PanNumber' | 'EmailId' | 'MobileNo',
    value: string,
    form: FormGroup,
    userIdField?: string
  ) {
    if (!value) return;

    const formValues = form.value;
    const userId = userIdField ? form.get(userIdField)?.value : formValues.userId;

    const data: UserMasterValidateUniqueIdentitiesRequestDTO = {
      userId: userId || null,
      aadharNumber: field === 'AadharNumber' ? value : formValues.aadharNumber ?? '',
      panNumber: field === 'PanNumber' ? value : formValues.panNumber ?? '',
      //emailId: field === 'EmailId' ? value : formValues.emailId ?? '',
      //mobileNo: field === 'MobileNo' ? value : formValues.mobileNo ?? ''
    };

    this.userMasterService.UserMaster_ValidateUniqueIdentities(data).subscribe({
      next: (res) => {
        // Clear previous duplicate errors
        ['aadharNumber', 'panNumber', ].forEach((f) => {
          const control = form.get(f);
          if (control?.hasError('duplicate')) {
            const currentErrors = { ...control.errors };
            delete currentErrors['duplicate'];
            control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
          }
        });

        switch (res.statusCode) {
          case 'OK':
            break;

          case 'INPUT_ERROR':
            this.toastr.warning(res.message || 'Please provide at least one identifier.', 'Validation');
            break;

          case 'USER_ALREADY_EXISTS':
            this.toastr.info(res.message || 'User already exists. Please link or login.', 'Existing User');
            break;

          case 'DUPLICATE_ON_CREATE':
          case 'DUPLICATE_ON_UPDATE':
            if (Array.isArray(res.conflicts) && res.conflicts.length > 0) {
              res.conflicts.forEach((conflict: any) => {
                const fieldName = conflict.field.charAt(0).toLowerCase() + conflict.field.slice(1);
                const conflictCtrl = form.get(fieldName);
                if (conflictCtrl) {
                  conflictCtrl.markAsTouched();
                  conflictCtrl.setErrors({ ...(conflictCtrl.errors || {}), duplicate: true });
                }
                const message = conflict.errorText || this.getDuplicateMessage(conflict.field, conflict.value);
                this.toastr.warning(message, 'Duplicate Detected');
              });
            } else {
              this.toastr.warning(res.message || 'Duplicate detected in one or more fields.', 'Validation');
            }
            break;

          default:
            this.toastr.error('Unexpected validation response from server.', 'Error');
            break;
        }


        if (Array.isArray(res.conflicts)) {
          const matched = res.conflicts.find((c: any) => c.existingUserId);
          if (matched) {
            const userId = Number(matched.existingUserId);

            this.onUserDetected.next(userId);
          }
        }
      },
      error: (err) => {
        console.error('Validation SP Error:', err);
        this.toastr.error('Server error during identity validation.', 'Error');
      }
    });
  }

  private getDuplicateMessage(field: string, value: string): string {
    switch (field) {
      case 'AadharNumber': return `Aadhaar number ${value} is already associated with another user.`;
      case 'PanNumber': return `PAN ${value} is already associated with another user.`;
      case 'EmailId': return `Email ${value} is already registered with another account.`;
      case 'MobileNo': return `Mobile number ${value} is already registered.`;
      default: return `${field} is already in use.`;
    }
  }
}
